import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function EntityMismatchDiagnosticPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.entityMismatchAudit();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const diverged = results?.properties?.filter(p => p.entity_id_vs_uuid_diverged) || [];
  const mismatched = results?.properties?.filter(p => p.is_region_entity_mismatch) || [];
  const allProps = results?.properties || [];
  const entitiesWithProblems = results?.entities_with_problems || [];

  const hasProblem = diverged.length > 0 || mismatched.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Entity Mismatch Audit (property entity_id vs entity_uuid)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Compares the entity the UI displays (entity_uuid / entity_name) against the
          entity_id type-code that the mismatch detector and invoicing routing read.
          Highlights properties where the PATCH endpoint wrote a wrong entity_id
          (e.g. "eci") while entity_uuid points to the correct French entity.
        </p>

        <Button onClick={runAudit} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Audit
        </Button>

        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`rounded-lg border p-4 ${hasProblem ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : 'border-green-500 bg-green-50 dark:bg-green-950/20'}`}>
              <div className="flex items-center gap-2 mb-2">
                {hasProblem ? (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                )}
                <span className="font-medium">
                  {hasProblem ? 'Divergences detected' : 'No divergences found'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Properties scanned</span>
                  <div className="font-mono font-medium">{results.total_properties}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">entity_id vs entity_uuid diverged</span>
                  <div className={`font-mono font-medium ${diverged.length > 0 ? 'text-red-600' : ''}`}>
                    {results.entity_id_vs_uuid_divergences}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Region/entity mismatches</span>
                  <div className={`font-mono font-medium ${mismatched.length > 0 ? 'text-red-600' : ''}`}>
                    {results.region_entity_mismatches}
                  </div>
                </div>
              </div>
            </div>

            {/* db.entities audit (full scan) */}
            {entitiesWithProblems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2 text-red-600">
                  db.entities with canonical mismatches ({entitiesWithProblems.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border">
                    <thead className="bg-muted">
                      <tr>
                        <th className="p-2 text-left">UUID</th>
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-left">Stored type</th>
                        <th className="p-2 text-left">Canonical type (from name)</th>
                        <th className="p-2 text-left">Problem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entitiesWithProblems.map((e, i) => (
                        <tr key={i} className="bg-red-50 dark:bg-red-950/20">
                          <td className="p-2 font-mono">{e.uuid}</td>
                          <td className="p-2">{e.name}</td>
                          <td className="p-2 font-mono">{e.stored_type || '(null)'}</td>
                          <td className="p-2 font-mono">{e.canonical_type_from_name || '(no match)'}</td>
                          <td className="p-2">
                            <Badge variant="destructive" className="text-[10px]">
                              {e.problem === 'name_not_in_canonical_registry' ? 'NAME MISMATCH' : 'TYPE MISMATCH'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Diverged properties (highlight) */}
            {diverged.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2 text-red-600">
                  Diverged properties (entity_id != expected type from entity_name)
                </h3>
                <PropertyTable rows={diverged} highlight />
              </div>
            )}

            {/* Mismatched but not diverged */}
            {mismatched.filter(m => !m.entity_id_vs_uuid_diverged).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2 text-amber-600">
                  Region mismatches (no UUID divergence)
                </h3>
                <PropertyTable rows={mismatched.filter(m => !m.entity_id_vs_uuid_diverged)} />
              </div>
            )}

            {/* All properties table */}
            <details>
              <summary className="text-sm font-medium cursor-pointer">
                All {allProps.length} portfolio properties
              </summary>
              <div className="mt-2">
                <PropertyTable rows={allProps} />
              </div>
            </details>

            {/* Correction Preview/Apply */}
            {diverged.length > 0 && <EntityIdCorrectionCard />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EntityIdCorrectionCard() {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    setApplied(null);
    try {
      const { data } = await diagnosticsAPI.entityIdCorrectionPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    setApplyLoading(true);
    try {
      const { data } = await diagnosticsAPI.entityIdCorrectionApply();
      setApplied(data);
      setPreview(null);
      toast.success(
        `Corrected ${data.properties_corrected} properties, re-stamped ${data.reservations_restamped} reservations`
      );
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-500 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Wrench className="h-5 w-5 text-amber-500" />
        <h3 className="font-semibold text-sm">Correct Diverged entity_id Values</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Re-derives entity_id from entity_name via the canonical registry for all
        diverged properties, then re-stamps the corrected entity_id on their
        reservations. This fixes invoicing eligibility and region/entity mismatch warnings.
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={runPreview} disabled={previewLoading || applyLoading}>
          {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
          Preview
        </Button>
        {preview && preview.diverged_count > 0 && !applied && (
          <Button size="sm" variant="destructive" onClick={runApply} disabled={applyLoading}>
            {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Wrench className="h-3 w-3 mr-1" />}
            Apply ({preview.diverged_count} properties, {preview.total_reservations_to_restamp} reservations)
          </Button>
        )}
      </div>

      {preview && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border">
            <thead className="bg-muted">
              <tr>
                <th className="p-2 text-left">Property</th>
                <th className="p-2 text-left">Platform</th>
                <th className="p-2 text-left">entity_id before</th>
                <th className="p-2 text-left">entity_id after</th>
                <th className="p-2 text-left">Reservations</th>
                <th className="p-2 text-left">Invoicing change</th>
              </tr>
            </thead>
            <tbody>
              {preview.items?.map((item, i) => (
                <tr key={i} className="bg-amber-50 dark:bg-amber-950/20">
                  <td className="p-2 font-medium">{item.property_name}</td>
                  <td className="p-2">{item.platform}</td>
                  <td className="p-2 font-mono text-red-600">{item.current_entity_id} ({item.current_entity_label})</td>
                  <td className="p-2 font-mono text-green-600">{item.correct_entity_id} ({item.correct_entity_label})</td>
                  <td className="p-2 font-mono">{item.reservation_count}</td>
                  <td className="p-2">
                    <span className="text-red-600">{item.invoicing_change?.before_label}</span>
                    {' -> '}
                    <span className="text-green-600">{item.invoicing_change?.after_label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {applied && (
        <div className="rounded border border-green-500 bg-green-50 dark:bg-green-950/20 p-3">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="font-medium text-sm">Correction applied</span>
          </div>
          <div className="text-xs space-y-1">
            <div>Properties corrected: <span className="font-mono">{applied.properties_corrected}</span></div>
            <div>Reservations re-stamped: <span className="font-mono">{applied.reservations_restamped}</span></div>
          </div>
          {applied.details?.length > 0 && (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs border">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-1 text-left">Property</th>
                    <th className="p-1 text-left">Before</th>
                    <th className="p-1 text-left">After</th>
                    <th className="p-1 text-left">Reservations</th>
                  </tr>
                </thead>
                <tbody>
                  {applied.details.map((d, i) => (
                    <tr key={i}>
                      <td className="p-1">{d.property_name}</td>
                      <td className="p-1 font-mono text-red-600">{d.entity_id_before}</td>
                      <td className="p-1 font-mono text-green-600">{d.entity_id_after}</td>
                      <td className="p-1 font-mono">{d.reservations_restamped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PropertyTable({ rows, highlight = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border">
        <thead className="bg-muted">
          <tr>
            <th className="p-2 text-left">Property</th>
            <th className="p-2 text-left">Platform</th>
            <th className="p-2 text-left">Region</th>
            <th className="p-2 text-left">UI entity (entity_name)</th>
            <th className="p-2 text-left">entity_id (type-code)</th>
            <th className="p-2 text-left">Detector resolves</th>
            <th className="p-2 text-left">Invoicing</th>
            <th className="p-2 text-left">Booking entity_ids</th>
            <th className="p-2 text-left">Flags</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const rowClass = highlight || r.entity_id_vs_uuid_diverged
              ? 'bg-red-50 dark:bg-red-950/20'
              : r.is_region_entity_mismatch
                ? 'bg-amber-50 dark:bg-amber-950/20'
                : '';
            return (
              <tr key={i} className={rowClass}>
                <td className="p-2 font-medium">{r.property_name || r.property_id}</td>
                <td className="p-2">{r.platform}</td>
                <td className="p-2">{r.region}</td>
                <td className="p-2">{r.property_entity_name || '(none)'}</td>
                <td className="p-2 font-mono">
                  {r.property_entity_id || '(none)'}
                  {r.entity_id_vs_uuid_diverged && (
                    <span className="text-red-600 ml-1">(expected: {r.expected_type_from_name})</span>
                  )}
                </td>
                <td className="p-2">{r.detector_resolved_entity || '(none)'}</td>
                <td className="p-2">{r.invoicing_entity_label}</td>
                <td className="p-2 font-mono">
                  {r.booking_entity_ids?.map((b, j) => (
                    <div key={j}>
                      {b.entity_id}: {b.count}
                      {b.entity_id !== r.expected_type_from_name && r.expected_type_from_name && (
                        <span className="text-amber-600 ml-1">(stale)</span>
                      )}
                    </div>
                  ))}
                  {(!r.booking_entity_ids || r.booking_entity_ids.length === 0) && '(no bookings)'}
                </td>
                <td className="p-2 space-y-0.5">
                  {r.entity_id_vs_uuid_diverged && (
                    <Badge variant="destructive" className="text-[10px]">DIVERGED</Badge>
                  )}
                  {r.is_region_entity_mismatch && (
                    <Badge variant="outline" className="border-amber-500 text-amber-600 text-[10px]">MISMATCH</Badge>
                  )}
                  {r.region === 'fr' && !r.invoicing_entity && r.property_entity_uuid && (
                    <Badge variant="outline" className="border-red-500 text-red-600 text-[10px]">NOT INVOICEABLE</Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
